/**
 * The platform console's Products sheet, its pure part (migration 0227; plan of record
 * docs/plans/2026-10-13_microvision-and-the-account-layer.md, section A): an org's entitlement to each suite product as
 * a form, the PUT body it makes, what is wrong with it before it is sent, and the server's refusals in words. Only a
 * platform admin grants a product.
 */
import { format, parseISO } from "date-fns";
import type { AdminModelTraining, AdminOrgSuiteProduct, AdminOrgSuiteProductPut } from "@/lib/api/platform-admin";
import { errorCodeOf, errorDetailOf, isNetworkError, statusOf } from "@/lib/api/error";

export const orgProductsKey = (orgId: string) => ["admin", "org-products", orgId] as const;

export type ProductStatus = AdminOrgSuiteProductPut["status"];

/** One product's entitlement as the form edits it: until as a plant-free calendar date, seats as typed. */
export interface ProductForm {
  status: ProductStatus;
  /** "YYYY-MM-DD", or empty for no end. */
  until: string;
  plan: string;
  seats: string;
}

export const STATUS_WORDS: Record<ProductStatus, string> = {
  active: "Active",
  trial: "Trial",
  suspended: "Suspended",
};

/** The form of a product as the org holds it; a product it does not hold starts as a 30 day trial from today. */
export function formOfProduct(p: AdminOrgSuiteProduct, today: Date = new Date()): ProductForm {
  if (!p.held) {
    const end = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    return { status: "trial", until: format(end, "yyyy-MM-dd"), plan: "", seats: "" };
  }
  return {
    status: p.status ?? "active",
    until: p.until ? p.until.slice(0, 10) : "",
    plan: p.plan ?? "",
    seats: p.seats != null ? String(p.seats) : "",
  };
}

/** The end of the given day in UTC: a trial "until 12 November" runs through that day. */
export function untilOf(date: string): string | null {
  return date ? `${date}T23:59:59Z` : null;
}

/** What is wrong with the form before it is sent, or null. The server checks the same, and has the last word. */
export function productFormProblem(f: ProductForm, now: Date = new Date()): string | null {
  if (f.status === "trial" && !f.until) return "A trial needs an end date.";
  const until = untilOf(f.until);
  if (until && f.status !== "suspended" && new Date(until).getTime() <= now.getTime()) return "The end date has passed.";
  if (f.seats.trim() !== "") {
    const n = Number(f.seats.trim());
    if (!Number.isInteger(n) || n <= 0 || n > 2147483647) return "Seats is a whole number above zero, or empty for no limit.";
  }
  if ([...f.plan].length > 64) return "The plan name is at most 64 characters.";
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f-\u009f]/.test(f.plan)) return "The plan name holds a character that cannot be shown.";
  return null;
}

/** The PUT body: the whole entitlement, since the request replaces it. */
export function productBody(f: ProductForm): AdminOrgSuiteProductPut {
  const seats = f.seats.trim();
  return {
    status: f.status,
    plan: f.plan.trim(),
    seats: seats === "" ? null : Number(seats),
    until: untilOf(f.until),
  };
}

/** How the org holds a product, in a line. */
export function holdingWords(p: AdminOrgSuiteProduct): string {
  if (!p.held) return "Not held";
  const parts = [STATUS_WORDS[p.status ?? "active"]];
  // The end's calendar date as stored (the close of that day in UTC), the same date the form edits.
  if (p.until) parts.push(`until ${format(parseISO(p.until.slice(0, 10)), "d MMM yyyy")}`);
  if (p.seats != null) parts.push(`${p.seats} seat${p.seats === 1 ? "" : "s"}`);
  if (p.plan) parts.push(p.plan);
  if (p.held && !p.usable) parts.push("not usable now");
  return parts.join(", ");
}

/** The opt-in's record, in a line. */
export function trainingWords(t: AdminModelTraining): string {
  if (!t.opt_in) return t.at ? `Off since ${format(new Date(t.at), "d MMM yyyy")}` : "Off";
  return t.at ? `On since ${format(new Date(t.at), "d MMM yyyy")}` : "On";
}

export function productsErrorWords(err: unknown, orgName: string): string {
  const status = statusOf(err);
  const code = errorCodeOf(err);
  if (status === 401) return "Your session has ended: sign in again.";
  if (status === 403) return "Only a platform admin can see or change an org's products.";
  if (code === "org_not_found") return `${orgName} no longer exists.`;
  if (code === "unknown_product") return "The platform does not know that product.";
  if (status === 400) return errorDetailOf(err) ?? "The server did not take that. Check the fields and try again.";
  if (isNetworkError(err)) return "The platform could not be reached. Check the connection and try again.";
  return "Something went wrong on the platform. Try again in a moment.";
}
